"use client";

import { useState, useEffect } from "react";
import { PAYPAL_V6_SCRIPT_URL } from "@/lib/config";
import {
  retryTransient,
  TransientHttpError,
  type RetryInfo,
} from "@/lib/payments/transient-retry";

interface UsePayPalSDKOptions {
  currencyCode: string;
  /** Set to false to delay initialization (e.g. form not ready) */
  enabled?: boolean;
  /**
   * Optional telemetry hook fired once per transient retry on either the
   * SDK script load OR the `/api/paypal/client-token` fetch. Default fires
   * `console.warn` only. Callers ship the event to monitoring (Axiom etc.)
   * to verify the hotfix actually helped on the next drop.
   *
   * @see .tondocs/paypal/HANDOFF_DROP_RESILIENCE_2026_06_26.md T3
   */
  onRetry?: (info: RetryInfo) => void;
}

interface UsePayPalSDKReturn {
  sdk: PayPalV6Instance | null;
  isLoading: boolean;
  isPayPalEligible: boolean;
  isApplePayEligible: boolean;
  isGooglePayEligible: boolean;
  error: string | null;
}

// Singleton: ensure the v6 core script is loaded exactly once
let scriptLoadPromise: Promise<void> | null = null;

function loadPayPalScript(): Promise<void> {
  if (scriptLoadPromise) return scriptLoadPromise;

  // Already loaded (e.g. via a <script> tag in HTML)
  if (typeof window !== "undefined" && window.paypal?.createInstance) {
    return Promise.resolve();
  }

  scriptLoadPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = PAYPAL_V6_SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptLoadPromise = null; // allow retry
      reject(new Error("Failed to load PayPal v6 SDK script"));
    };
    document.head.appendChild(script);
  });

  return scriptLoadPromise;
}

/**
 * Hook for managing the PayPal v6 SDK lifecycle.
 *
 * 1. Loads the v6 core script (singleton)
 * 2. Fetches a client token from /api/paypal/client-token
 * 3. Creates an SDK instance via window.paypal.createInstance()
 * 4. Checks payment method eligibility
 */
export function usePayPalSDK({
  currencyCode,
  enabled = true,
  onRetry,
}: UsePayPalSDKOptions): UsePayPalSDKReturn {
  const [sdk, setSdk] = useState<PayPalV6Instance | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPayPalEligible, setIsPayPalEligible] = useState(false);
  const [isApplePayEligible, setIsApplePayEligible] = useState(false);
  const [isGooglePayEligible, setIsGooglePayEligible] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 1: Initialize SDK instance (runs once when enabled)
  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;

    // T3 (HANDOFF_DROP_RESILIENCE_2026_06_26): the SDK init path has two
    // transient failure classes on hot drops —
    //   (1) PayPal v6 script load flake (mobile network / in-app browser),
    //   (2) `/api/paypal/client-token` hitting the Supabase refresh-token
    //       race (76× `auth.session_cleared` on the 2026-06-26 drop).
    // Both are wrapped by `retryTransient` — single shared policy (2
    // attempts / 400ms / 401/5xx/network = transient) imported from
    // `lib/payments/transient-retry.ts`, observable via `onRetry`. Replaces
    // the H2 inline-sleep hotfix (`190c24ca`).

    // Callback wrapper that fires both the default console.warn AND the
    // caller-supplied `onRetry` (so external telemetry sees it too). Latest
    // value of the prop is read via closure on each retry; the effect re-
    // runs only on `enabled` change, so the closure is intentionally stable
    // within an init attempt.
    const onRetryBridge = (info: RetryInfo): void => {
      // eslint-disable-next-line no-console
      console.warn(
        `[PayPal SDK] retry_fired ${info.stage} attempt=${info.attempt}`,
        info.error,
      );
      onRetry?.(info);
    };

    async function initSDK() {
      try {
        setIsLoading(true);
        setError(null);

        // Load v6 core script via the shared retry helper.
        // `loadPayPalScript` already nulls `scriptLoadPromise` on error so a
        // second call kicks off a fresh script element. Cancellation is
        // checked after the helper returns (the helper does not own the
        // React lifecycle flag).
        await retryTransient(loadPayPalScript, {
          stage: "script_load",
          onRetry: onRetryBridge,
        });
        if (cancelled) return;

        // Fetch client token via the shared retry helper.
        // The wrapper throws `TransientHttpError` on 401/5xx so the helper
        // classifies them as transient; bare network throws stay transient
        // by default. Other 4xx (real client bugs) propagate as the
        // original Response.ok=false → permanent → no retry.
        const fetchClientTokenStrict = async (): Promise<{
          clientToken: string;
          components: string[];
        }> => {
          const res = await fetch("/api/paypal/client-token", {
            cache: "no-store",
          });
          if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            // 401/5xx → TransientHttpError → retried; other 4xx →
            // TransientHttpError too but classifier returns "permanent" →
            // single throw. Single error class keeps the catch shape uniform.
            const snippet =
              typeof body?.error === "string"
                ? body.error
                : JSON.stringify(body).slice(0, 200);
            throw new TransientHttpError(res.status, snippet);
          }
          return res.json();
        };

        const { clientToken, components } = await retryTransient(
          fetchClientTokenStrict,
          {
            stage: "client_token",
            onRetry: onRetryBridge,
          },
        );
        if (cancelled) return;

        console.log("[PayPal SDK] Got client token (JWT prefix):", clientToken?.substring(0, 20) + "...");
        console.log("[PayPal SDK] Components:", components);

        // Create SDK instance
        const instance = await window.paypal!.createInstance({
          clientToken,
          components,
          pageType: "checkout",
        });
        if (cancelled) return;

        console.log("[PayPal SDK] Instance created successfully");
        setSdk(instance);
      } catch (err: any) {
        if (!cancelled) {
          console.error("[PayPal SDK] Init failed:", err);
          setError(err.message || "Failed to initialize PayPal");
          setIsLoading(false);
        }
      }
    }

    initSDK();
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  // Step 2: Check eligibility (when SDK is ready or currency changes)
  useEffect(() => {
    if (!sdk) return;

    let cancelled = false;

    async function checkEligibility() {
      try {
        setIsLoading(true);
        const methods = await sdk!.findEligibleMethods({ currencyCode });
        if (!cancelled) {
          const paypal = methods.isEligible("paypal");
          const applepay = methods.isEligible("applepay");
          const googlepay = methods.isEligible("googlepay");
          console.log("[PayPal SDK] Eligibility check:", {
            currencyCode,
            paypal,
            card: methods.isEligible("card"),
            venmo: methods.isEligible("venmo"),
            applepay,
            googlepay,
          });
          setIsPayPalEligible(paypal);
          setIsApplePayEligible(applepay);
          setIsGooglePayEligible(googlepay);
          setIsLoading(false);
        }
      } catch (err: any) {
        if (!cancelled) {
          // findEligibleMethods can 403 in sandbox (missing app features) or
          // for PPCP platform accounts. Default to eligible — the actual
          // payment session will surface real errors if PayPal is unavailable.
          console.warn("[PayPal SDK] Eligibility check failed, defaulting to eligible:", err.message || err);
          setIsPayPalEligible(true);
          setIsLoading(false);
        }
      }
    }

    checkEligibility();
    return () => {
      cancelled = true;
    };
  }, [sdk, currencyCode]);

  return { sdk, isLoading, isPayPalEligible, isApplePayEligible, isGooglePayEligible, error };
}
